/**
 * CoachPlaybook - Auth Callback
 * Version: 0.6.0
 * Description: Discord OAuth callback handler — exchanges code for tokens and redirects
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../utils/auth/AuthContext';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: '#1e293b',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    border: '1px solid #334155',
    textAlign: 'center'
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #334155',
    borderTop: '4px solid #5865F2',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 20px'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#f8fafc',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: 0
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '12px'
  },
  errorMsg: {
    fontSize: '14px',
    color: '#f87171',
    margin: '0 0 20px 0',
    lineHeight: '1.5'
  },
  retryBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#5865F2',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    padding: '12px 28px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s'
  }
};

// Inject keyframes for spinner
const styleSheet = typeof document !== 'undefined' && (() => {
  const existing = document.getElementById('auth-callback-styles');
  if (existing) return existing.sheet;
  const el = document.createElement('style');
  el.id = 'auth-callback-styles';
  el.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(el);
  return el.sheet;
})();

const AuthCallback = () => {
  const { exchangeCode, login } = useAuth();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const errorParam = params.get('error');

      if (errorParam) {
        setError(`Discord authorization denied: ${errorParam}`);
        setProcessing(false);
        return;
      }

      if (!code) {
        setError('No authorization code received from Discord.');
        setProcessing(false);
        return;
      }

      try {
        await exchangeCode(code);
        // Clean URL and redirect to app root
        window.history.replaceState({}, '', '/');
        window.location.reload();
      } catch (err) {
        setError(err.message || 'Failed to complete authentication.');
        setProcessing(false);
      }
    };

    handleCallback();
  }, [exchangeCode]);

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>{'\u26A0\uFE0F'}</div>
          <h2 style={styles.title}>Authentication Failed</h2>
          <p style={styles.errorMsg}>{error}</p>
          <button
            style={styles.retryBtn}
            onClick={login}
            onMouseEnter={(e) => { e.target.style.background = '#4752c4'; }}
            onMouseLeave={(e) => { e.target.style.background = '#5865F2'; }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {processing && <div style={styles.spinner} />}
        <h2 style={styles.title}>Signing you in...</h2>
        <p style={styles.subtitle}>Completing Discord authentication</p>
      </div>
    </div>
  );
};

export default AuthCallback;
